import * as React from 'react';
import {
  SURFACE_GLOSS_FILTER_ID,
  SURFACE_GLOSS_GRADIENT_ID,
  SURFACE_GLOSS_MASK_ID,
  SURFACE_GLOSS_MASK_ALPHA_FILTER_ID,
  SURFACE_GLOSS_ITEMS_ID,
} from '../../utils/surfaceFilter';
import { MIN_WORLD_UNIT_MM } from '../../utils/editorUnits';
import type { Bounds } from '../../Reducers/Configurator/types';

type SurfaceGlossFilterProps = {
  blurRadius: number;
  region: Bounds;
  designBounds: Bounds;
};

/**
 * High-Gloss Glass & Polished Acrylic Material Engine.
 * Utilizes feMorphology to shift reflections inside the stroke faces,
 * completely eliminating flat cartoon outlines and text color bleaching.
 */
export default function SurfaceGlossFilter({ blurRadius, region, designBounds }: SurfaceGlossFilterProps) {
  const r = Math.max(blurRadius, MIN_WORLD_UNIT_MM);
  const gradientY1 = designBounds.y;
  const gradientY2 = designBounds.y + Math.max(designBounds.height, MIN_WORLD_UNIT_MM);

  return (
    <defs>
      <filter
        id={SURFACE_GLOSS_FILTER_ID}
        filterUnits="userSpaceOnUse"
        x={region.x}
        y={region.y}
        width={Math.max(region.width, MIN_WORLD_UNIT_MM)}
        height={Math.max(region.height, MIN_WORLD_UNIT_MM)}
        colorInterpolationFilters="sRGB"
      >
        <feDropShadow
          dx={r * 0.35}
          dy={r * 0.55}
          stdDeviation={r * 0.90}
          floodColor="#000000"
          floodOpacity="0.35"
          result="externalShadow"
        />

        <feMorphology operator="erode" radius={r * 0.18} in="SourceAlpha" result="innerFace" />
        <feGaussianBlur in="innerFace" stdDeviation={r * 0.12} result="innerBlur" />

        <feOffset dx={r * 0.14} dy={r * 0.14} in="innerBlur" result="innerShiftHi" />
        <feComposite in="innerBlur" in2="innerShiftHi" operator="out" result="glintRaw" />
        <feComposite in="glintRaw" in2="SourceAlpha" operator="in" result="glintMasked" />

        <feComponentTransfer in="glintMasked" result="crispGlint">
          <feFuncA type="gamma" amplitude="3.5" exponent="2.5" />
        </feComponentTransfer>
        <feFlood floodColor="#ffffff" floodOpacity="0.9" result="whiteLight" />
        <feComposite in="whiteLight" in2="crispGlint" operator="in" result="finalGlassGlint" />

        <feOffset dx={-r * 0.12} dy={-r * 0.12} in="innerBlur" result="innerShiftLo" />
        <feComposite in="innerBlur" in2="innerShiftLo" operator="out" result="shadowRaw" />
        <feComposite in="shadowRaw" in2="SourceAlpha" operator="in" result="shadowMasked" />
        <feFlood floodColor="#000214" floodOpacity="0.5" result="darkDensity" />
        <feComposite in="darkDensity" in2="shadowMasked" operator="in" result="finalInnerShadow" />

        <feMerge>
          <feMergeNode in="externalShadow" />
          <feMergeNode in="SourceGraphic" />
          <feMergeNode in="finalInnerShadow" />
          <feMergeNode in="finalGlassGlint" />
        </feMerge>
      </filter>

      <filter id={SURFACE_GLOSS_MASK_ALPHA_FILTER_ID}>
        <feFlood floodColor="#ffffff" />
        <feComposite in2="SourceAlpha" operator="in" />
      </filter>

      <linearGradient
        id={SURFACE_GLOSS_GRADIENT_ID}
        gradientUnits="userSpaceOnUse"
        x1={0}
        y1={gradientY1}
        x2={0}
        y2={gradientY2}
      >
        <stop offset="0%" stopColor="#ffffff" stopOpacity={0.22} />
        <stop offset="20%" stopColor="#ffffff" stopOpacity={0.0} />
        <stop offset="80%" stopColor="#ffffff" stopOpacity={0.0} />
        <stop offset="100%" stopColor="#ffffff" stopOpacity={0.12} />
      </linearGradient>

      <mask
        id={SURFACE_GLOSS_MASK_ID}
        maskUnits="userSpaceOnUse"
        x={region.x}
        y={region.y}
        width={Math.max(region.width, MIN_WORLD_UNIT_MM)}
        height={Math.max(region.height, MIN_WORLD_UNIT_MM)}
      >
        <g filter={`url(#${SURFACE_GLOSS_MASK_ALPHA_FILTER_ID})`} pointerEvents="none">
          <use href={`#${SURFACE_GLOSS_ITEMS_ID}`} pointerEvents="none" />
        </g>
      </mask>
    </defs>
  );
}
